import React, { Dispatch, SetStateAction, useState, useRef } from 'react';
import {
	Box,
	Button,
	ClickAwayListener,
	Typography,
	useTheme,
	Checkbox,
	Stack,
} from '@mui/material';
import { useGlobalState } from '../../../../stateProviders/GlobalStateProvider.tsx';
import CustomSwitch from '../../../../globalComponents/toggleButton.tsx';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
	useQuoteDispatch,
	useQuoteState,
} from '../../../../stateProviders/QuoteStateProvider.tsx';
import { useApi } from '../../../../hooks/useApi.ts';
import Role from '../../../../enums/role.ts';
import { toastError, toastSuccess } from '../../../../Toaster/Toast.tsx';
import { ErrorToast } from 'src/Toaster/Toaster.tsx';
import AmountInput from 'src/globalComponents/amountInput.tsx';

const ProposalModeMenu: React.FC<{
	quoteId: string;
	pricingOption: any;
	optionComplete: boolean;
	proposalMode: boolean;
	setProposalMode: Dispatch<SetStateAction<boolean>>;
	setShowMenu: Dispatch<SetStateAction<boolean>>;
	stackMode: boolean;
	setStackMode: Dispatch<SetStateAction<boolean>>;
}> = ({
	quoteId,
	pricingOption,
	optionComplete,
	proposalMode,
	setProposalMode,
	setShowMenu,
	stackMode,
	setStackMode,
}) => {
	const theme = useTheme();
	const globalState = useGlobalState();
	const quoteState = useQuoteState();
	const quoteDispatch = useQuoteDispatch();
	const api = useApi();
	const { appUser } = useGlobalState();
	const downloadLinkRef = useRef<HTMLAnchorElement>(null);
	const [managerDiscount, setManagerDiscount] = useState(
		quoteState.managerDiscount
	);
	const [isEditingDiscount, setIsEditingDiscount] = useState(false);
	const [managerDiscountType, setManagerDiscountType] = useState<'%' | '$'>(
		'%'
	);

	const [takeOffGenerating, setTakeOffGenerating] = useState(false);

	if (!appUser) return <></>;

	const generateTakeOff = async () => {
		if (!optionComplete) {
			toastSuccess({
				title: 'Cannot generate takeoff',
				description: 'This option is incomplete',
			});
			return;
		}

		setTakeOffGenerating(true);

		const result = await api.takeoff.createTakeoff(quoteId, pricingOption);

		if (result.errored) {
			// ErrorToast('Takeoff generation failed. Try again later');
			toastSuccess({
				title: 'Takeoff generation failed',
				description: 'Try again later',
			});
			setTakeOffGenerating(false);
		} else {
			const downloadUrl = await api.takeoff.viewTakeoff(result._id);

			if (downloadUrl.errored) {
				ErrorToast('Takeoff generation failed. Try again later');
				return;
			}

			if (downloadLinkRef.current) {
				const link = downloadLinkRef.current;

				const res = await fetch(downloadUrl, { credentials: 'omit' });
				const blob = await res.blob();
				const url = URL.createObjectURL(blob);

				link.href = url;
				link.download =
					`${quoteState.currentQuote?.customerName}_${selectedOption.deckingType}_${selectedOption.railingType}.xlsx`.replace(
						/\s+/g,
						'_'
					);

				setTakeOffGenerating(false);
				toastSuccess({
					title: 'Takeoff Generated',
					description:
						'You have successfully generated the takeoff for this option',
				});

				link.click();
			}
		}
	};

	const optionCanSelect = (i: number) => {
		for (let x = 0; x < i; x++) {
			if (
				!quoteState.appliedDiscounts
					.map((ad) => ad.discountName)
					.includes(globalState.company?.discounts?.[x]?.discountName || '')
			)
				return false;
		}

		return true;
	};

	const optionCanUnselect = (discountName: string) => {
		const lastSelected =
			quoteState.appliedDiscounts[quoteState.appliedDiscounts.length - 1];
		return discountName == lastSelected.discountName;
	};

	const optionIsDisabled = (i: number, discountName: string) => {
		if (!stackMode) return false;

		const isSelected = quoteState.appliedDiscounts
			.map((ad) => ad.discountName)
			.includes(discountName);

		if (!isSelected) {
			return !optionCanSelect(i);
		}

		return !optionCanUnselect(discountName);
	};

	const selectedOption =
		quoteState.options[quoteState.selectedOptionIndex || 0];

	const canEditManagerDiscount = [
		Role.SUPER_ADMIN.toString(),
		Role.COMPANY_ADMIN.toString(),
		Role.SALES_MANAGER.toString(),
	].includes(appUser.role.roleName);

	const showManagerDiscount =
		canEditManagerDiscount || quoteState.managerDiscount;

	let totalDiscountedPrice = pricingOption?.totalClientCost;

	quoteState.appliedDiscounts.forEach((discount) => {
		totalDiscountedPrice = Math.round(
			totalDiscountedPrice * (1 - discount.discountPercentage / 100)
		);
	});

	return (
		<ClickAwayListener onClickAway={() => setShowMenu(false)}>
			<Box
				sx={{
					position: 'absolute',
					top: 50,
					right: 0,
					boxShadow: (theme) => theme.shadows[3],
					padding: (theme) => theme.spacing(2),
					borderRadius: (theme) => theme.shape.borderRadius,
					backgroundColor: (theme) => theme.palette.background.paper,
					border: (theme) => `1px solid ${theme.palette.divider}`,
					width: '300px',
					zIndex: 1000,
				}}
			>
				<a
					ref={downloadLinkRef}
					target="_blank"
					rel="noreferer"
					style={{ visibility: 'hidden', position: 'absolute' }}
				>
					Download
				</a>

				<Box
					sx={{
						width: '100%',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography sx={{ fontWeight: 600 }}>Proposal Mode</Typography>

					<CustomSwitch
						onChange={() => {
							setProposalMode((prev) => {
								setShowMenu(false);

								if (!prev) {
									api.handleEvent.proposalModeEnabled(quoteId);
								}

								return !prev;
							});
						}}
						on={proposalMode}
						disabled={!globalState.company?.isDocubeeTenant}
						// disabledTooltip={
						// 	globalState.company?.isDocubeeTenant
						// 		? undefined
						// 		: 'Proposal Mode requires a Docubee account. Please speak with your admin to get started.'
						// }
					/>
				</Box>

				{!globalState.company?.isDocubeeTenant && (
					<Typography
						sx={{
							color: theme.palette.gray[500],
						}}
					>
						<strong>Upgrade DeckMetrix</strong> to unlock
					</Typography>
				)}

				<Box
					sx={{
						width: '100%',
						marginTop: '15px',
						marginBottom: '10px',
					}}
				>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<Typography sx={{ fontWeight: 600 }}>Apply Discounts in Order</Typography>
						<CustomSwitch
							on={stackMode}
							onChange={() => {
								setStackMode((prev) => {
									if (!prev) {
										quoteDispatch({ appliedDiscounts: [] });
									}
									return !prev;
								});
							}}
						/>
					</Box>
					<Typography sx={{ fontSize: '12px', color: theme.palette.gray[500], lineHeight: 1.6, mt: '4px' }}>
						Turn on the toggle to apply discounts<br />one at a time, from top to bottom. Turn it off to apply discounts in any order.
					</Typography>
				</Box>

				<Typography sx={{ fontWeight: 600, marginBottom: '10px' }}>
					Apply Discounts
				</Typography>

				<Box>
					{globalState.company?.discounts.map((discount, i) => {
						const disabled = optionIsDisabled(i, discount.discountName);

						const checked = quoteState.appliedDiscounts
							.map((ad) => ad.discountName)
							.includes(discount.discountName);

						return (
							<Box
								key={i}
								sx={{
									display: 'flex',
									justifyContent: 'start',
									alignItems: 'center',
									gap: '5px',
								}}
							>
								<Checkbox
									sx={{
										transform: 'scale(1.3)',
										'& .MuiSvgIcon-root': {
											fontSize: 'inherit',
										},
									}}
									checked={checked}
									color="primary"
									disabled={disabled}
									onChange={() => {
										if (checked) {
											quoteDispatch({
												appliedDiscounts: quoteState.appliedDiscounts.filter(
													(ad) => ad.discountName != discount.discountName
												),
											});
										} else {
											quoteDispatch({
												appliedDiscounts: [
													...quoteState.appliedDiscounts,
													discount,
												],
											});
										}
									}}
								/>
								<Typography
									sx={{
										color: disabled ? '#BCBECC' : '#3F4247',
									}}
								>
									{discount.discountName}
								</Typography>
							</Box>
						);
					})}

					{showManagerDiscount &&
						(() => {
							const checked =
								quoteState.managerDiscount != undefined ||
								managerDiscount != undefined;

							const disabled = !canEditManagerDiscount;

							return (
								<Stack>
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'start',
											alignItems: 'center',
											gap: '5px',
										}}
									>
										<Checkbox
											sx={{
												transform: 'scale(1.3)',
												'& .MuiSvgIcon-root': {
													fontSize: 'inherit',
												},
											}}
											checked={checked}
											color="primary"
											disabled={disabled}
											onChange={() => {
												if (checked) {
													setManagerDiscount(undefined);
													quoteDispatch({
														managerDiscount: undefined,
													});
													setIsEditingDiscount(false);
												} else {
													setManagerDiscount({
														percentage: 0,
														managerId: appUser._id,
														date: new Date(),
													});
													setIsEditingDiscount(true);
												}
											}}
										/>
										<Typography
											sx={{
												color: disabled ? '#BCBECC' : '#3F4247',
											}}
										>
											Manager's Discount
										</Typography>

										{checked && (
											<Typography
												color={disabled ? '#BCBECC' : 'green'}
												sx={{
													cursor: 'pointer',
													'&:hover': {
														textDecoration: disabled ? 'none' : 'underline',
													},
												}}
												onClick={() => setIsEditingDiscount(true)}
											>
												({quoteState.managerDiscount?.percentage || 0}%)
											</Typography>
										)}
									</Box>

									{canEditManagerDiscount &&
										managerDiscount &&
										isEditingDiscount && (
											<>
												{managerDiscountType == '%' &&
													managerDiscount.percentage > 100 && (
														<Typography
															sx={{
																color: 'red',
																fontSize: '12px',
																my: '5px',
															}}
														>
															Discount percentage cannot be greater than 100%
														</Typography>
													)}

												{managerDiscountType == '$' &&
													managerDiscount.percentage > totalDiscountedPrice && (
														<Typography
															sx={{
																color: 'red',
																fontSize: '12px',
																my: '5px',
															}}
														>{`Discount amount cannot be greater than ${totalDiscountedPrice}`}</Typography>
													)}
												<Box
													sx={{
														display: 'grid',
														gridTemplateColumns: '3fr 1fr',
														gap: '5px',
													}}
												>
													<AmountInput
														value={managerDiscount?.percentage}
														inputType={managerDiscountType}
														maxAmount={
															managerDiscountType == '%'
																? 100
																: totalDiscountedPrice
														}
														onChange={(value, inputType) => {
															setManagerDiscountType(inputType);
															setManagerDiscount({
																...managerDiscount,
																percentage: value,
															});
														}}
													/>

													<Button
														onClick={() => {
															if (
																managerDiscountType == '%' &&
																managerDiscount.percentage > 100
															) {
																toastError({
																	title:
																		'Discount percentage cannot be greater than 100%',
																});
																return;
															} else if (
																managerDiscount.percentage >
																totalDiscountedPrice
															) {
																toastError({
																	title: `Discount amount cannot be greater than ${totalDiscountedPrice}`,
																});
																return;
															}

															let percentage = managerDiscount.percentage;

															if (managerDiscountType == '$') {
																percentage = parseFloat(
																	(
																		(managerDiscount.percentage /
																			totalDiscountedPrice) *
																		100
																	).toFixed(2)
																);
															}

															quoteDispatch({
																managerDiscount: {
																	...managerDiscount,
																	percentage,
																},
															});

															setIsEditingDiscount(false);
														}}
														sx={{
															backgroundColor: 'primary.main',
															color: 'white',
														}}
													>
														{quoteState.managerDiscount ? 'Update' : 'Apply'}
													</Button>
												</Box>
											</>
										)}
								</Stack>
							);
						})()}
				</Box>

				<Box
					sx={{
						width: '100%',
						marginTop: '30px',
						display: 'flex',
						justifyContent: 'end',
					}}
				>
					{(appUser?.role?.roleName == Role.SALES_MANAGER ||
						appUser?.role?.roleName == Role.COMPANY_ADMIN ||
						appUser?.role?.roleName == Role.SUPER_ADMIN) && (
						<Button
							sx={{ color: 'secondary.main' }}
							endIcon={<ArrowForwardIcon sx={{ color: 'secondary.main' }} />}
							disabled={takeOffGenerating}
							onClick={generateTakeOff}
						>
							{takeOffGenerating
								? 'Generating Takeoff ...'
								: 'Generate Takeoff'}
							(.xls)
						</Button>
					)}
				</Box>
			</Box>
		</ClickAwayListener>
	);
};

export default ProposalModeMenu;
